import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import T2TSidebar from "./T2TSidebar";
import { T2TUIProvider, usePermissions, useToast } from "./T2TUIProvider";
import { getData } from "../../../FetchApiServices";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../T2T.css";

// ── Bottom Tab Bar (Mobile only) ──
const BottomTabBar = ({ queueCount }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const showTeam = hasPermission("reports.view");
  const isActive = (path) => location.pathname === path;

  return (
    <nav className="t2t-bottom-tab-bar">
      <button className={`t2t-tab-item${isActive("/t2t-home") ? " active" : ""}`} onClick={() => navigate("/t2t-home")}>
        <i className="bi bi-house-fill"></i><span>Home</span>
      </button>
      <button className={`t2t-tab-item${isActive("/t2t-queue") ? " active" : ""}`} onClick={() => navigate("/t2t-queue")}>
        <i className="bi bi-inbox-fill"></i><span>Queue</span>
        {queueCount > 0 && <span className="t2t-tab-badge">{queueCount > 99 ? "99+" : queueCount}</span>}
      </button>
      <button className="t2t-tab-item t2t-tab-center" onClick={() => navigate("/t2t-requests")}>
        <i className="bi bi-plus-lg"></i>
      </button>
      {showTeam && (
        <button className={`t2t-tab-item${isActive("/t2t-reports") ? " active" : ""}`} onClick={() => navigate("/t2t-reports")}>
          <i className="bi bi-bar-chart-fill"></i><span>Team</span>
        </button>
      )}
      <button className={`t2t-tab-item${isActive("/t2t-admin") ? " active" : ""}`} onClick={() => navigate("/t2t-admin")}>
        <i className="bi bi-person-fill"></i><span>Profile</span>
      </button>
    </nav>
  );
};

BottomTabBar.propTypes = { queueCount: PropTypes.number };

// A failed product switch comes back as a redirect carrying ?switch_error=...
// (switcher doc §9: every refusal must land on a page WE render, never a raw
// central error). Turn it into a toast and strip the param so a refresh does
// not replay the message.
const SWITCH_ERRORS = {
  no_access:       "You don't have access to that application. Contact your administrator to request it.",
  not_provisioned: "Your account isn't set up for that application yet. Contact your administrator.",
  not_signed_in:   "Your session expired. Please sign in again.",
  bad_request:     "That application link looks malformed.",
  sso_disabled:    "Single sign-on isn't enabled in this environment.",
  try_later:       "Couldn't switch applications just now — please try again shortly.",
};

const SwitchErrorToast = () => {
  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get("switch_error");
    if (!code) return;

    toast(SWITCH_ERRORS[code] || SWITCH_ERRORS.try_later, "error");

    params.delete("switch_error");
    const qs = params.toString();
    navigate(`${location.pathname}${qs ? `?${qs}` : ""}`, { replace: true });
  }, [location.search, location.pathname, toast, navigate]);

  return null;
};

const closeSidebarDOM = () => {
  document.getElementById("t2t-sidebar")?.classList.remove("open");
  document.getElementById("t2t-backdrop")?.classList.remove("open");
};

const T2TLayout = () => {
  const [queueCount, setQueueCount] = useState(0);

  const isFioriEmbed = window.self !== window.top &&
    new URLSearchParams(window.location.search).get("sap-fiori") === "true";

  useEffect(() => {
    const fetchCount = async () => {
      const resp = await getData("t2t/queue");
      if (!resp.error) {
        const raw = resp.data?.data;
        setQueueCount(Array.isArray(raw) ? raw.length : 0);
      }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, []);

  if (isFioriEmbed) {
    return (
      <T2TUIProvider>
        <SwitchErrorToast />
        <div className="t2t-module" style={{ background: "var(--t2t-bg)" }}>
          <div className="t2t-main" style={{ marginLeft: 0 }}>
            <Outlet />
          </div>
        </div>
      </T2TUIProvider>
    );
  }

  return (
    <T2TUIProvider>
      <SwitchErrorToast />
      <div className="t2t-module">
        {/* Backdrop — DOM-controlled (.open added by T2TTopNav.openSidebar) */}
        <div id="t2t-backdrop" className="t2t-sidebar-backdrop" onClick={closeSidebarDOM} />

        {/* Sidebar overlay — DOM-controlled via id="t2t-sidebar" */}
        <T2TSidebar queueCount={queueCount} onClose={closeSidebarDOM} />

        {/* Full-width main area — pages render their own T2TTopNav */}
        <div className="t2t-main">
          <Outlet />
        </div>

        <BottomTabBar queueCount={queueCount} />
      </div>
    </T2TUIProvider>
  );
};

export default T2TLayout;
